import type { Reward, Artist } from "@/mocks/artistData";
import { getScheduleStatus, getDDay, formatDate, getDaysUntilOpen } from "@/utils/rewardSchedule";

interface Props {
  reward: Reward;
  artist?: Artist;
  myXP: number;
  onSelect: () => void;
}

const badgeStyle: Record<string, string> = {
  LEGENDARY: "bg-[#ffd60a]/20 text-[#ffd60a] border-[#ffd60a]/50",
  "ULTRA RARE": "bg-[#f72585]/20 text-[#f72585] border-[#f72585]/50",
  RARE: "bg-[#4fc3f7]/20 text-[#4fc3f7] border-[#4fc3f7]/50",
};

export default function RewardCard({ reward, artist, myXP, onSelect }: Props) {
  const scheduleStatus = getScheduleStatus(reward);
  const isExpired = scheduleStatus === "expired";
  const isUpcoming = scheduleStatus === "upcoming";
  const isActive = scheduleStatus === "active";

  const canRedeem = myXP >= reward.xpCost && isActive;
  const stockPercent = Math.round((reward.stock / reward.totalStock) * 100);
  const isSoldOut = reward.stock === 0;
  const dday = getDDay(reward);

  return (
    <button
      onClick={isExpired ? undefined : onSelect}
      className={`relative rounded-2xl border bg-[#12121a] overflow-hidden text-left group transition-all duration-300 w-full ${
        isExpired
          ? "border-white/5 opacity-50 cursor-not-allowed"
          : isUpcoming
          ? "border-white/10 hover:border-white/20 cursor-pointer hover:scale-[1.02]"
          : canRedeem && !isSoldOut
          ? "border-[#39ff14]/40 hover:border-[#39ff14]/70 cursor-pointer hover:scale-[1.02]"
          : "border-white/10 hover:border-white/20 cursor-pointer hover:scale-[1.02]"
      }`}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
        <img
          src={reward.image}
          alt={reward.title}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${!isExpired ? "group-hover:scale-105" : ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12121a]/90 via-transparent to-transparent" />

        {/* expired 오버레이 */}
        {isExpired && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="px-4 py-1.5 rounded-full bg-white/10 text-white/60 text-xs font-bold border border-white/20 tracking-widest">
              EXPIRED
            </span>
          </div>
        )}

        {/* upcoming 오버레이 */}
        {isUpcoming && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="text-center">
              <div className="text-white/80 text-xs font-semibold mb-1">오픈 예정</div>
              <div className="text-white font-black text-lg">D-{getDaysUntilOpen(reward)}</div>
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {reward.badge && (
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border ${badgeStyle[reward.badge] ?? ""}`}>
              {reward.badge}
            </span>
          )}
          {isSoldOut && isActive && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/50 border border-white/20">
              SOLD OUT
            </span>
          )}
        </div>

        {/* D-Day 배지 */}
        {isActive && !isSoldOut && (
          <div className="absolute top-3 right-3">
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                dday === "D-Day"
                  ? "bg-[#f72585]/20 text-[#f72585] border-[#f72585]/40"
                  : parseInt(dday.replace("D-", "")) <= 7
                  ? "bg-[#ff8c42]/20 text-[#ff8c42] border-[#ff8c42]/40"
                  : "bg-white/10 text-white/50 border-white/20"
              }`}
            >
              {dday}
            </span>
          </div>
        )}

        {/* Category Label */}
        <div className="absolute bottom-3 left-3">
          <span
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
            style={{ background: `${reward.color}20`, color: reward.color, border: `1px solid ${reward.color}40` }}
          >
            <i className={`${reward.icon} mr-1`} />
            {reward.categoryLabel}
          </span>
        </div>

        {/* Can Redeem Glow */}
        {canRedeem && !isSoldOut && isActive && (
          <div className="absolute bottom-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-[#39ff14]/20 border border-[#39ff14]/50">
            <i className="ri-check-line text-[#39ff14] text-xs" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Artist */}
        {artist && (
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
              <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
            </div>
            <span className="text-white/40 text-xs">{artist.name}</span>
            {artist.verified && <i className="ri-verified-badge-fill text-xs" style={{ color: artist.accentColor }} />}
          </div>
        )}

        <h3 className="text-white font-bold text-sm mb-2 line-clamp-2 leading-snug">{reward.title}</h3>
        <p className="text-white/40 text-xs line-clamp-2 mb-4 leading-relaxed">{reward.description}</p>

        {/* Perks */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {reward.perks.slice(0, 3).map((perk, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md text-[10px] bg-white/5 text-white/40 border border-white/10">
              {perk}
            </span>
          ))}
          {reward.perks.length > 3 && (
            <span className="px-2 py-0.5 rounded-md text-[10px] bg-white/5 text-white/30 border border-white/10">
              +{reward.perks.length - 3}
            </span>
          )}
        </div>

        {/* Stock Bar */}
        {!isExpired && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-white/30 text-[10px]">잔여 수량</span>
              <span className="text-white/50 text-[10px] font-semibold">
                {reward.stock.toLocaleString()} / {reward.totalStock.toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${stockPercent}%`,
                  background: stockPercent > 30 ? reward.color : "#f72585",
                }}
              />
            </div>
          </div>
        )}

        {/* XP Cost & CTA */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <i className="ri-coin-fill text-[#ffd60a] text-sm" />
            <span className={`font-black text-base ${isExpired ? "text-white/30" : "text-[#ffd60a]"}`}>
              {reward.xpCost.toLocaleString()}
            </span>
            <span className="text-white/30 text-xs">XP</span>
          </div>
          <span
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              isExpired
                ? "bg-white/5 text-white/20 border border-white/5"
                : isUpcoming
                ? "bg-white/5 text-white/40 border border-white/10"
                : isSoldOut
                ? "bg-white/5 text-white/30 cursor-not-allowed"
                : canRedeem
                ? "bg-[#39ff14]/20 text-[#39ff14] border border-[#39ff14]/40"
                : "bg-white/5 text-white/40 border border-white/10"
            }`}
          >
            {isExpired ? "기간 종료" : isUpcoming ? "오픈 예정" : isSoldOut ? "품절" : canRedeem ? "교환 가능" : "XP 부족"}
          </span>
        </div>

        {/* 제공 기간 */}
        <div className="mt-3 flex items-center gap-1.5 text-white/25 text-[10px]">
          <i className="ri-calendar-line" />
          <span>{formatDate(reward.startDate)} ~ {formatDate(reward.endDate)}</span>
        </div>
      </div>
    </button>
  );
}
